import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Award, Download, FileSpreadsheet, Loader2, Mail, RefreshCw, Upload, Users, X, CloudUpload } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError } from "../../lib/api";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const COLS = [["fecha", "Fecha"], ["ticket", "Ticket"], ["asistencia", "Asist."], ["nodo", "Nodo"],
  ["patente", "Patente"], ["tecnico", "Técnico"], ["ayudante", "Ayudante"], ["operador", "Operador"],
  ["turno", "Turno"], ["estado", "Estado"], ["inicio", "Inicio"], ["cierre", "Cierre"],
  ["creado", "Creado"], ["descarga", "Desc. mat."], ["pago", "Pago"], ["sigla", "Sigla"],
  ["solapa", "Solapa"], ["valor_pond", "V. pond."], ["peso_nodo", "Peso nodo"], ["vpt", "V.P. total"]];
const selCls = "bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet";
const btnCls = "flex items-center gap-2 text-sm rounded-lg border border-navy/10 bg-white px-3 py-2 hover:border-violet/40 transition-colors disabled:opacity-50";
const money = (v) => v == null ? "—" : `$ ${Number(v).toLocaleString("es-AR", { minimumFractionDigits: 2 })}`;

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="bg-white border border-navy/10 rounded-2xl p-5" data-testid={testid}>
      <div className="text-3xl font-semibold text-navy">{value}</div>
      <div className="text-sm text-navy/50 mt-1">{label}</div>
      {sub && <div className="text-xs text-navy/35 mt-0.5">{sub}</div>}
    </div>
  );
}

function MiniTable({ title, icon: Icon, headers, rows, testid, onRowClick }) {
  return (
    <div className="bg-white border border-navy/10 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-violet" />
        <h3 className="font-semibold text-navy text-sm">{title}</h3>
      </div>
      <div className="overflow-auto max-h-80">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-navy/40 border-b border-navy/10">
            {headers.map((h) => <th key={h} className="py-1.5 pr-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} onClick={onRowClick ? () => onRowClick(i) : undefined}
                className={`border-b border-navy/5 last:border-0 ${onRowClick ? "cursor-pointer hover:bg-violet/5" : ""}`}>
                {r.map((c, j) => <td key={j} className={`py-1.5 pr-3 ${j === 0 ? "font-medium" : "text-navy/60"}`}>{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TecnicoModal({ nombre, mes, year, pago, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/tickets-control/tecnico", { params: { nombre, mes, year, pago } })
      .then((r) => setD(r.data)).catch(() => toast.error("No se pudo cargar la ficha"));
  }, [nombre, mes, year, pago]);
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-10" onClick={onClose} data-testid="tecnico-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-navy">{nombre}</h2>
            <p className="text-xs text-navy/40">Ficha del técnico · {mes} {year}</p>
          </div>
          <button onClick={onClose} className="text-navy/40 hover:text-navy" data-testid="tecnico-modal-close"><X className="w-5 h-5" /></button>
        </div>
        {!d ? <div className="py-10 text-center text-navy/40 text-sm"><Loader2 className="w-5 h-5 animate-spin inline" /> Cargando…</div> : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Kpi label="Tickets completados" value={d.cantidad} testid="tec-kpi-cantidad" />
              <Kpi label="Ponderado del mes" value={d.ponderado} testid="tec-kpi-ponderado" />
              <Kpi label={`Productividad a pagar (>${d.umbral})`}
                value={<span className={d.exceso_50 > 0 ? "text-emerald-600" : ""}>{d.exceso_50 > 0 ? d.exceso_50 : "—"}</span>}
                sub={d.exceso_50 > 0 ? "tickets ponderados sobre el umbral" : `no superó los ${d.umbral} ponderados`} testid="tec-kpi-exceso" />
            </div>
            <div className="bg-white border border-navy/10 rounded-2xl p-4">
              <div className="text-sm font-semibold text-navy mb-2">Evolución diaria (ponderado)</div>
              <div style={{ height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.por_dia}>
                    <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={32} />
                    <Tooltip formatter={(v, n) => [v, n === "ponderado" ? "Ponderado" : n]} labelFormatter={(l) => `Día ${l}`} />
                    <Bar dataKey="ponderado" fill="#6C5CE7" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <MiniTable title="Por tipo de ticket" icon={FileSpreadsheet} testid="tec-por-tipo"
                headers={["Sigla", "Cantidad", "Ponderado"]}
                rows={(d.por_tipo || []).map((t) => [t.sigla, t.cantidad, t.ponderado])} />
              <MiniTable title={`Tickets del mes (${d.tickets.length})`} icon={Award} testid="tec-tickets"
                headers={["Fecha", "Ticket", "Nodo", "Sigla", "V.P.T."]}
                rows={(d.tickets || []).map((t) => [t.fecha, t.ticket, t.nodo, t.sigla, t.vpt])} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function TicketsControl() {
  const now = new Date();
  const [mes, setMes] = useState(MESES[now.getMonth()]);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [writing, setWriting] = useState(false);
  const [sendingInforme, setSendingInforme] = useState(false);
  const [uploadingMontos, setUploadingMontos] = useState(false);
  const [tecnico, setTecnico] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [pago, setPago] = useState("produccion");
  const fileRef = useRef(null);
  const reqId = useRef(0);

  const load = useCallback(async (refresh = false) => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const r = await api.get("/tickets-control/resumen", { params: { mes, year, refresh, pago, limit: 10000 } });
      if (id !== reqId.current) return;
      setData(r.data);
      if (r.data.stale_error) toast.error(`Mostrando datos previos: ${r.data.stale_error}`);
    } catch (e) {
      if (id !== reqId.current) return;
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo leer la planilla");
    } finally { if (id === reqId.current) setLoading(false); }
  }, [mes, year, pago]);
  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/tickets-control/export", { params: { mes, year, pago }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `control_tickets_${pago}_${mes}_${year}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo exportar"); }
    finally { setExporting(false); }
  };

  const subirMontos = async (f) => {
    if (!f) return;
    setUploadingMontos(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/tickets-control/montos/upload", fd);
      toast.success(`Planilla de montos cargada: ${r.data.tipos} tipos de ticket`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo leer la planilla de montos");
    } finally { setUploadingMontos(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const volcarSheet = async () => {
    if (!window.confirm(`Se creará/actualizará la solapa "Control ${mes} ${year}" en la planilla CORRECTIVOS de Google. ¿Continuar?`)) return;
    setWriting(true);
    try {
      const r = await api.post("/tickets-control/write-sheet", null, { params: { mes, year } });
      toast.success(`Listo: solapa "${r.data.solapa}" con ${r.data.filas} filas en CORRECTIVOS`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo escribir en Google Sheets", { duration: 9000 });
    } finally { setWriting(false); }
  };

  const enviarInforme = async () => {
    setSendingInforme(true);
    try {
      const r = await api.post("/tickets-control/informe", null, { params: { mes, year } });
      toast.success(`Informe de cierre enviado a: ${r.data.enviados.join(", ")}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo enviar el informe", { duration: 8000 });
    } finally { setSendingInforme(false); }
  };

  const res = data?.resumen || {};
  const esProd = pago === "produccion";
  const montosOn = esProd && !!res.montos_info;
  return (
    <OSLayout title="OMNIA Tickets">
      <div className="space-y-6" data-testid="tickets-control-page">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-navy/10 overflow-hidden" data-testid="tc-pago-tabs">
            <button onClick={() => setPago("produccion")} data-testid="tc-pago-produccion"
              className={`px-4 py-2 text-sm transition-colors ${esProd ? "bg-navy text-white" : "bg-white text-navy/60 hover:text-navy"}`}>Por producción</button>
            <button onClick={() => setPago("abono")} data-testid="tc-pago-abono"
              className={`px-4 py-2 text-sm transition-colors ${!esProd ? "bg-navy text-white" : "bg-white text-navy/60 hover:text-navy"}`}>Incluído en abono</button>
          </div>
          <select value={mes} onChange={(e) => setMes(e.target.value)} className={selCls} data-testid="tc-mes-select">
            {MESES.map((m) => <option key={m} value={m}>{m[0].toUpperCase() + m.slice(1)}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={selCls} data-testid="tc-year-select">
            {[now.getFullYear() + 1, now.getFullYear(), now.getFullYear() - 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => load(true)} disabled={loading} data-testid="tc-refresh-btn" className={btnCls}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Actualizar desde la hoja
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={uploadingMontos} data-testid="tc-montos-btn" className={btnCls}>
            {uploadingMontos ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {montosOn ? "Reemplazar planilla de montos" : "Subir planilla de montos"}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="tc-montos-input"
            onChange={(e) => subirMontos(e.target.files?.[0])} />
          <button onClick={volcarSheet} disabled={writing || !data} data-testid="tc-write-sheet-btn" className={btnCls}>
            {writing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />} Volcar a Google Sheet
          </button>
          <button onClick={enviarInforme} disabled={sendingInforme || !data} data-testid="tc-informe-btn" className={btnCls}>
            {sendingInforme ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Enviar informe
          </button>
          <button onClick={exportXlsx} disabled={exporting || !data} data-testid="tc-export-btn"
            className="flex items-center gap-2 text-sm rounded-lg bg-navy text-white px-4 py-2 hover:bg-navy/90 transition-colors disabled:opacity-50">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Exportar Excel
          </button>
          {data?.built_at && <span className="text-xs text-navy/35">actualizado {new Date(data.built_at).toLocaleString("es-AR")} · {data.solapas?.length || 0} solapas leídas</span>}
        </div>
        <p className="text-xs text-navy/35 -mt-3" data-testid="tc-auto-close-note">
          Cierre automático: el día 1 de cada mes OMNIA vuelca la solapa "Control {"{mes anterior}"}" en CORRECTIVOS y envía el informe de productividad a los correos de alertas de Flotilla.
        </p>

        {montosOn && (
          <div className="text-xs text-navy/45 -mt-3" data-testid="tc-montos-info">
            Montos cargados desde <span className="font-medium">{res.montos_info.filename}</span> ({res.montos_info.count} tipos)
            {res.tipos_sin_monto?.length > 0 && <span className="text-amber-600"> · sin monto: {res.tipos_sin_monto.join(", ")}</span>}
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center gap-2 text-navy/50 text-sm py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Leyendo solapas de operadores…</div>
        ) : data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label={`Tickets completados (${esProd ? "producción" : "abono"})`} value={res.total_tickets ?? 0} sub={`${mes} ${year}`} testid="tc-kpi-total" />
              <Kpi label="Valor ponderado total" value={res.total_ponderado ?? 0} testid="tc-kpi-ponderado" />
              <Kpi label="Técnicos sobre 50 pond." value={res.tecnicos_sobre_50 ?? 0} sub="cobran productividad" testid="tc-kpi-sobre50" />
              <Kpi label="Monto a facturar" value={montosOn ? money(res.monto_a_facturar) : "—"}
                sub={!esProd ? "no aplica (incluído en abono)" : montosOn ? `según planilla de montos` : "sube la planilla de montos por tipo"} testid="tc-kpi-monto" />
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
              <MiniTable title="Por técnico (clic para abrir ficha)" icon={Award} testid="tc-tabla-tecnicos"
                headers={["Técnico", "Tickets", "Ponderado", "Exceso >50"]}
                onRowClick={(i) => setTecnico(res.por_tecnico[i].tecnico)}
                rows={(res.por_tecnico || []).map((t) => [t.tecnico, t.cantidad, t.ponderado,
                  t.exceso_50 > 0 ? <span key="e" className="text-emerald-600 font-semibold">{t.exceso_50}</span> : "—"])} />
              <MiniTable title="Por tipo de ticket (sigla)" icon={FileSpreadsheet} testid="tc-tabla-tipos"
                headers={montosOn ? ["Sigla", "Cant.", "Pond.", "Monto unit.", "Monto total"] : ["Sigla", "Cantidad", "Valor unit.", "Ponderado"]}
                rows={(res.por_tipo || []).map((t) => montosOn
                  ? [t.sigla, t.cantidad, t.ponderado, money(t.monto_unit), money(t.monto_total)]
                  : [t.sigla, t.cantidad, t.valor_unit, t.ponderado])} />
              <MiniTable title="Por operador (solapa)" icon={Users} testid="tc-tabla-operadores"
                headers={["Operador", "Tickets", "Ponderado"]}
                rows={(res.por_operador || []).map((t) => [t.operador, t.cantidad, t.ponderado])} />
            </div>

            {res.nodos_sin_peso?.length > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="tc-nodos-sin-peso">
                {res.nodos_sin_peso.length}{res.nodos_sin_peso.length >= 50 ? "+" : ""} nodos sin peso en "BASE DE DATOS NODOS PESO" (se usó peso 1): {res.nodos_sin_peso.slice(0, 10).join(", ")}…
              </div>
            )}

            <div className="bg-white border border-navy/10 rounded-2xl p-5" data-testid="tc-planilla">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h3 className="font-semibold text-navy text-sm">Planilla · Control de tickets completados</h3>
                <div className="flex items-center gap-3">
                  <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Filtrar por ticket, técnico, nodo, patente, sigla…"
                    className="bg-white border border-navy/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-violet w-72" data-testid="tc-filtro-input" />
                  <span className="text-xs text-navy/40" data-testid="tc-filas-info">
                    {(() => {
                      const q = filtro.trim().toLowerCase();
                      const shown = q ? (data.rows || []).filter((r) => [r.ticket, r.tecnico, r.ayudante, r.nodo, r.patente, r.sigla, r.solapa, r.operador].join(" ").toLowerCase().includes(q)).length : (data.rows || []).length;
                      return q ? `${shown} de ${data.total_rows} filas (filtradas)` : `${data.total_rows} filas (todas)`;
                    })()}
                  </span>
                </div>
              </div>
              <div className="overflow-auto max-h-[520px]">
                <table className="w-full text-xs whitespace-nowrap">
                  <thead className="sticky top-0 bg-white"><tr className="text-left text-navy/40 border-b border-navy/10">
                    {COLS.map(([k, l]) => <th key={k} className="py-2 pr-3 font-medium">{l}</th>)}
                  </tr></thead>
                  <tbody>
                    {(data.rows || []).filter((r) => {
                      const q = filtro.trim().toLowerCase();
                      if (!q) return true;
                      return [r.ticket, r.tecnico, r.ayudante, r.nodo, r.patente, r.sigla, r.solapa, r.operador].join(" ").toLowerCase().includes(q);
                    }).map((r, i) => (
                      <tr key={i} className="border-b border-navy/5 last:border-0" data-testid="tc-row">
                        {COLS.map(([k]) => <td key={k} className="py-1.5 pr-3 text-navy/70">{String(r[k] ?? "")}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        {tecnico && <TecnicoModal nombre={tecnico} mes={mes} year={year} pago={pago} onClose={() => setTecnico(null)} />}
      </div>
    </OSLayout>
  );
}
